import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, Icon, Modal, Pill, Select, SkeletonText, Textarea, useToast } from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { downloadPdf } from "../lib/export-pdf.js";
import "./project-status-tab.css";

/**
 * The project's weekly status: the latest update as a card, earlier weeks as a
 * timeline coloured by RAG, and — for the people allowed to — posting, the PDF
 * report and the public share link.
 */

export type RagHealth = "green" | "amber" | "red";

export type StatusUpdate = {
  id: string;
  authorName: string;
  period: string;
  health: RagHealth;
  summary: string;
  done: string;
  next: string;
  blockers: string;
  risks: string;
  updatedAt: string;
};

export const RAG_TONE: Record<RagHealth, PillTone> = { green: "ok", amber: "warn", red: "danger" };
export const RAG_LABEL: Record<RagHealth, string> = { green: "Green", amber: "Amber", red: "Red" };

type ShareLink = { id: string; createdAt: string; expiresAt: string | null; createdByName: string };

const PAGE = 10;

function weekLabel(period: string): string {
  return `Week of ${new Date(`${period}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

/** The four prose sections, skipping empty ones. Shared with the public page. */
export function UpdateBody({ update }: { update: StatusUpdate }) {
  const sections: [string, string][] = [
    ["Done", update.done],
    ["Next", update.next],
    ["Blockers", update.blockers],
    ["Risks", update.risks],
  ];
  return (
    <div className="status-update__body">
      <p className="status-update__summary">{update.summary}</p>
      {sections
        .filter(([, text]) => text.trim())
        .map(([label, text]) => (
          <section key={label}>
            <h4>{label}</h4>
            <p>{text}</p>
          </section>
        ))}
    </div>
  );
}

/* ───────────────────────────── post / edit form ───────────────────────────── */

function UpdateForm({
  projectId,
  editing,
  onSaved,
  onClose,
}: {
  projectId: string;
  /** This week's existing update, when there is one — the form then edits it. */
  editing: StatusUpdate | null;
  onSaved: () => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [health, setHealth] = useState<RagHealth>(editing?.health ?? "green");
  const [summary, setSummary] = useState(editing?.summary ?? "");
  const [done, setDone] = useState(editing?.done ?? "");
  const [next, setNext] = useState(editing?.next ?? "");
  const [blockers, setBlockers] = useState(editing?.blockers ?? "");
  const [risks, setRisks] = useState(editing?.risks ?? "");
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  // A new update starts from what the tasks say happened; the author edits it.
  const fillFromTasks = useCallback(() => {
    setDrafting(true);
    api
      .get<{ draft: { done: string; next: string; blockers: string } }>(`/projects/${projectId}/status-updates/draft`)
      .then(({ draft }) => {
        setDone(draft.done);
        setNext(draft.next);
        setBlockers(draft.blockers);
      })
      .catch(() => toast("Could not build a draft from the tasks.", { tone: "danger" }))
      .finally(() => setDrafting(false));
  }, [projectId, toast]);

  useEffect(() => {
    if (!editing) fillFromTasks();
  }, [editing, fillFromTasks]);

  const submit = () => {
    if (!summary.trim()) return;
    setSaving(true);
    const body = { health, summary, done, next, blockers, risks };
    const request = editing
      ? api.patch(`/projects/${projectId}/status-updates/${editing.id}`, body)
      : api.post(`/projects/${projectId}/status-updates`, body);
    request
      .then(() => {
        toast(editing ? "Update saved." : "Update posted.");
        onSaved();
        onClose();
      })
      .catch((error: unknown) =>
        toast(error instanceof ApiError ? error.message : "Could not save the update.", { tone: "danger" }),
      )
      .finally(() => setSaving(false));
  };

  return (
    <form
      className="status-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Select label="Health" value={health} onChange={(e) => setHealth(e.target.value as RagHealth)}>
        <option value="green">Green — on track</option>
        <option value="amber">Amber — at risk</option>
        <option value="red">Red — off track</option>
      </Select>
      <Textarea label="Summary" value={summary} maxRows={4} onChange={(e) => setSummary(e.target.value)} autoFocus required />
      <Textarea label="Done this week" value={done} maxRows={8} onChange={(e) => setDone(e.target.value)} />
      <Textarea label="Next week" value={next} maxRows={8} onChange={(e) => setNext(e.target.value)} />
      <Textarea label="Blockers" value={blockers} maxRows={8} onChange={(e) => setBlockers(e.target.value)} />
      <Textarea label="Risks" value={risks} maxRows={6} onChange={(e) => setRisks(e.target.value)} />
      <div className="status-form__actions">
        {editing && (
          <Button type="button" variant="ghost" loading={drafting} onClick={fillFromTasks}>
            Refill from tasks
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!summary.trim() || saving}>
          {editing ? "Save changes" : "Post update"}
        </Button>
      </div>
    </form>
  );
}

/* ───────────────────────────── share links ───────────────────────────── */

function ShareDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { toast } = useToast();
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [fresh, setFresh] = useState<string | null>(null);
  const [days, setDays] = useState("30");

  const load = useCallback(() => {
    api
      .get<{ links: ShareLink[] }>(`/projects/${projectId}/share-links`)
      .then((r) => setLinks(r.links))
      .catch(() => toast("Could not load share links.", { tone: "danger" }));
  }, [projectId, toast]);
  useEffect(() => load(), [load]);

  const create = () =>
    api
      .post<{ link: { path: string } }>(`/projects/${projectId}/share-links`, {
        expiresInDays: days === "never" ? null : Number(days),
      })
      .then(({ link }) => {
        setFresh(`${window.location.origin}${link.path}`);
        load();
      })
      .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not create the link.", { tone: "danger" }));

  const revoke = (id: string) =>
    api
      .delete(`/projects/${projectId}/share-links/${id}`)
      .then(() => {
        toast("Link revoked.");
        load();
      })
      .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not revoke the link.", { tone: "danger" }));

  return (
    <div className="status-share">
      <p className="status-share__note">
        Anyone with the link can read the latest status update and the headline numbers — no task titles, no sign-in.
      </p>
      <div className="status-share__create">
        <Select label="Expires" value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="7">In 7 days</option>
          <option value="30">In 30 days</option>
          <option value="90">In 90 days</option>
          <option value="never">Never</option>
        </Select>
        <Button onClick={() => void create()}>Create link</Button>
      </div>
      {fresh && (
        <div className="status-share__fresh">
          <code>{fresh}</code>
          <Button
            size="sm"
            variant="secondary"
            icon={<Icon name="copy" size={13} />}
            onClick={() => void navigator.clipboard?.writeText(fresh).then(() => toast("Link copied."))}
          >
            Copy
          </Button>
          <small>This is the only time the full link is shown.</small>
        </div>
      )}
      <ul className="status-share__list">
        {links.map((link) => (
          <li key={link.id}>
            <span>
              Created {new Date(link.createdAt).toLocaleDateString()} by {link.createdByName}
              {link.expiresAt ? ` · expires ${new Date(link.expiresAt).toLocaleDateString()}` : " · never expires"}
            </span>
            <Button size="sm" variant="ghost" onClick={() => void revoke(link.id)}>
              Revoke
            </Button>
          </li>
        ))}
      </ul>
      <div className="status-form__actions">
        <Button variant="ghost" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}

/* ───────────────────────────── the tab ───────────────────────────── */

export function ProjectStatusTab({
  projectId,
  projectCode,
  canPost,
  canShare,
  onPosted,
}: {
  projectId: string;
  projectCode: string;
  canPost: boolean;
  canShare: boolean;
  /** Posting changes the project's health, so the page above reloads. */
  onPosted: () => void;
}) {
  const { toast } = useToast();
  const [updates, setUpdates] = useState<StatusUpdate[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [thisWeekId, setThisWeekId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ updates: StatusUpdate[]; nextCursor: string | null }>(`/projects/${projectId}/status-updates?limit=${PAGE}`)
      .then((r) => {
        setUpdates(r.updates);
        setCursor(r.nextCursor);
        // The server is the judge of "this week" when saving; this only picks
        // whether the button offers an edit, and a stale guess ends in a 409.
        const newest = r.updates[0];
        const recent = newest && Date.now() - new Date(`${newest.period}T00:00:00`).getTime() < 7 * 86_400_000;
        setThisWeekId(recent ? newest.id : null);
      })
      .catch(() => toast("Could not load status updates.", { tone: "danger" }))
      .finally(() => setLoading(false));
  }, [projectId, toast]);
  useEffect(() => load(), [load]);

  const more = () => {
    if (!cursor) return;
    api
      .get<{ updates: StatusUpdate[]; nextCursor: string | null }>(
        `/projects/${projectId}/status-updates?limit=${PAGE}&before=${cursor}`,
      )
      .then((r) => {
        setUpdates((prev) => [...prev, ...r.updates]);
        setCursor(r.nextCursor);
      })
      .catch(() => toast("Could not load earlier updates.", { tone: "danger" }));
  };

  const report = () => {
    setReporting(true);
    downloadPdf(`/projects/${projectId}/report.pdf`, `${projectCode}-status-report.pdf`)
      .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not build the report.", { tone: "danger" }))
      .finally(() => setReporting(false));
  };

  const latest = updates[0];
  const thisWeek = updates.find((u) => u.id === thisWeekId) ?? null;

  return (
    <section className="status-tab">
      <div className="status-tab__actions">
        {canPost && (
          <Button size="sm" icon={<Icon name="plus" size={13} />} onClick={() => setFormOpen(true)}>
            {thisWeek ? "Edit this week" : "Post update"}
          </Button>
        )}
        <Button size="sm" variant="secondary" icon={<Icon name="download" size={14} />} loading={reporting} onClick={report}>
          PDF report
        </Button>
        {canShare && (
          <Button size="sm" variant="secondary" icon={<Icon name="link" size={14} />} onClick={() => setShareOpen(true)}>
            Share
          </Button>
        )}
      </div>

      {loading ? (
        <SkeletonText lines={5} />
      ) : !latest ? (
        <EmptyState
          icon="clock"
          title="No status updates yet"
          description="A short weekly update keeps everyone on the same page about how this project is going."
          action={
            canPost ? (
              <Button size="sm" onClick={() => setFormOpen(true)}>
                Post the first update
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <article className={`status-card status-card--${latest.health}`}>
            <header>
              <Pill tone={RAG_TONE[latest.health]} dot>
                {RAG_LABEL[latest.health]}
              </Pill>
              <span>
                {weekLabel(latest.period)} · {latest.authorName}
              </span>
            </header>
            <UpdateBody update={latest} />
          </article>

          {updates.length > 1 && (
            <>
              <h3 className="status-tab__heading">History</h3>
              <ol className="status-timeline">
                {updates.slice(1).map((u) => (
                  <li key={u.id} className={`status-timeline__item status-timeline__item--${u.health}`}>
                    <header>
                      <Pill tone={RAG_TONE[u.health]} dot>
                        {RAG_LABEL[u.health]}
                      </Pill>
                      <span>
                        {weekLabel(u.period)} · {u.authorName}
                      </span>
                    </header>
                    <UpdateBody update={u} />
                  </li>
                ))}
              </ol>
            </>
          )}
          {cursor && (
            <Button variant="ghost" size="sm" onClick={more}>
              Show earlier updates
            </Button>
          )}
        </>
      )}

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={thisWeek ? "Edit this week's update" : "Post a status update"}>
        <UpdateForm
          projectId={projectId}
          editing={thisWeek ?? null}
          onSaved={() => {
            load();
            onPosted();
          }}
          onClose={() => setFormOpen(false)}
        />
      </Modal>
      <Modal open={shareOpen} onClose={() => setShareOpen(false)} title="Share status">
        <ShareDialog projectId={projectId} onClose={() => setShareOpen(false)} />
      </Modal>
    </section>
  );
}
